import { AfterViewInit, Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AlertController, IonIcon, LoadingController } from "@ionic/angular/standalone";
import { Store } from "@ngrx/store";
import { DisplayService, ValidationUtil } from "@qid/core";
import { firstValueFrom } from "rxjs";
import { AuthSelectors } from "../../../authentication";
import {
  CollectionTemplate,
  CollectionTemplateActions,
  CollectionTemplateRepository, CreateCollectionTemplateDto
} from "../../../collection-template";
import { MasterTemplate } from "../../models/MasterTemplate";
import { MasterTemplateService } from "../services/master-template.service";

@Component({
  selector: 'browse-master-templates',
  templateUrl: './browse-master-templates.component.html',
  styleUrls: ['./browse-master-templates.component.scss'],
  standalone: true,
  imports: [
    IonIcon
  ]
})
export class BrowseMasterTemplatesComponent implements OnInit, AfterViewInit, OnDestroy {

  public masterTemplates: MasterTemplate[] = [];
  public canScrollLeft = false;
  public canScrollRight = false;

  @ViewChild('templateScrollTrack') templateScrollTrack?: ElementRef<HTMLElement>;

  @Output() onCreate: EventEmitter<CollectionTemplate> = new EventEmitter<CollectionTemplate>()

  private resizeObserver?: ResizeObserver;

  constructor(private masterTemplateService: MasterTemplateService,
    private alertController: AlertController,
    private collectionTemplateRepository: CollectionTemplateRepository,
    private displayService: DisplayService,
    private loadingController: LoadingController,
    private store: Store
  ) {
  }

  ngOnInit() {
    this.fetchMasterTemplates();
  }

  ngAfterViewInit() {
    this.updateScrollButtons();
    const track = this.templateScrollTrack?.nativeElement;
    if (track) {
      this.resizeObserver = new ResizeObserver(() => this.updateScrollButtons());
      this.resizeObserver.observe(track);
    }
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  onTemplateScroll() {
    this.updateScrollButtons();
  }

  scrollTemplates(direction: 'left' | 'right') {
    this.templateScrollTrack?.nativeElement.scrollBy({
      left: direction === 'left' ? -160 : 160,
      behavior: 'smooth',
    });
  }

  private updateScrollButtons() {
    const track = this.templateScrollTrack?.nativeElement;
    if (!track) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = track;
    const isScrollable = scrollWidth > clientWidth + 1;
    this.canScrollLeft = isScrollable && scrollLeft > 0;
    this.canScrollRight = isScrollable && scrollLeft + clientWidth < scrollWidth - 1;
    track.parentElement?.classList.toggle('template-scroll--scrolled', scrollLeft > 0);
  }

  async fetchMasterTemplates() {
    this.masterTemplates = await this.masterTemplateService.readAll(null);
    requestAnimationFrame(() => this.updateScrollButtons());
  }

  createTemplate = async (masterTemplateId: string) => {
    const template: CollectionTemplate = await this.masterTemplateService.clone(masterTemplateId)
    if (!template || !template._id) {
      await this.displayService.toast({
        message: "Form creation failed. Upgrade your plan to access more forms.",
        color: "danger"
      })
      return
    }
    await this.displayService.toast({
      message: 'Form Created',
      color: 'success',
      icon: 'checkmark-outline',
    });
    const account_id = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccountId));
    this.store.dispatch(CollectionTemplateActions.addRecord({ record: template, account_id: account_id }))
    this.onCreate.emit(template)
  }

  createCollectionTemplate = async () => {

    const alert = await this.alertController.create({
      header: "Create New Form",
      subHeader: 'Please enter a name for your new form.',
      inputs: [
        {
          label: "Form Name",
          placeholder: 'Form Name',
          name: 'formName'
        }
      ],
      buttons: [
        {
          text: "Cancel",
          role: "cancel"
        },
        {
          text: 'Create',
          handler: async (data) => {
            if (ValidationUtil.isStringEmpty(data.formName) || ValidationUtil.isOnlyWhitespace(data.formName)) {
              this.displayService.toast({ message: "Form name cannot be empty" })
              return false
            }
            let dto = new CreateCollectionTemplateDto();
            dto.purpose = data.formName;
            const loader = await this.loadingController.create({ message: "Creating Form" });
            loader.present();
            const template = await this.collectionTemplateRepository.create(dto);
            if (template) {
              loader.dismiss();
              this.displayService.toast({ message: 'Form Created', color: "success" });
              this.onCreate.emit(template);
            } else {
              loader.dismiss();
              this.displayService.toast({ message: "Form creation failed. Upgrade your plan to access more forms.", color: "danger" });
            }

          }
        }
      ]
    })

    alert.present();

  }


}
